import { formatDate } from './claimIntakeUtils';
import {
  runClaimIntakeOperation,
  type ClaimIntakeSession,
} from './claimIntakeAgentApi';

export type PreviousClaimRow = Readonly<{
  claimNumber: string;
  status: string;
  claimDate: string;
}>;

export type PreviousClaimsState = Readonly<{
  claims: PreviousClaimRow[];
  loading: boolean;
  error: string | null;
  loadedForContract: string | null;
}>;

export const INITIAL_PREVIOUS_CLAIMS_STATE: PreviousClaimsState = {
  claims: [],
  loading: false,
  error: null,
  loadedForContract: null,
};

export function hasPreviousClaimsReviewContent(
  state: PreviousClaimsState,
  contractNumber: string | undefined
): boolean {
  const cn = contractNumber?.trim();
  if (!cn || state.loadedForContract !== cn || state.loading) return false;
  return state.claims.length > 0 || Boolean(state.error);
}

export function readPreviousClaimsFetchError(err: unknown): string {
  if (err != null && typeof err === 'object' && 'error' in err && typeof (err as { error?: unknown }).error === 'string') {
    return (err as { error: string }).error;
  }
  if (err instanceof Error) return err.message;
  return 'Failed to load previous claims.';
}

function readClaimField(claim: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const val = claim[key];
    if (typeof val === 'string' && val.trim()) return val.trim();
  }
  return '';
}

function mapClaimToRow(claim: unknown): PreviousClaimRow | null {
  if (!claim || typeof claim !== 'object' || Array.isArray(claim)) return null;
  const record = claim as Record<string, unknown>;
  const claimNumber = readClaimField(record, 'claimNumber', 'claim_number');
  if (!claimNumber) return null;
  const status = readClaimField(record, 'claimStatus', 'claim_status', 'status');
  const rawDate = readClaimField(record, 'claimDate', 'claim_date');
  return {
    claimNumber,
    status: status || '—',
    claimDate: rawDate ? formatDate(rawDate) : '—',
  };
}

/** Parses `workflow_finished.outputs.text` from the previous_claims dify_stream event. */
export function parsePreviousClaimsResponse(data: unknown): PreviousClaimRow[] {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  const root = data as Record<string, unknown>;
  const dataBucket = root.data;
  let buckets: unknown[] = [];
  if (Array.isArray(dataBucket)) {
    buckets = dataBucket;
  } else if (dataBucket != null) {
    buckets = [dataBucket];
  }
  const rows: PreviousClaimRow[] = [];

  for (const bucket of buckets) {
    if (!bucket || typeof bucket !== 'object' || Array.isArray(bucket)) continue;
    const claims = (bucket as Record<string, unknown>).claims;
    if (!Array.isArray(claims)) continue;
    for (const claim of claims) {
      const row = mapClaimToRow(claim);
      if (row) rows.push(row);
    }
  }

  return rows;
}

export async function fetchPreviousClaimsViaDifyStream(params: {
  session: ClaimIntakeSession;
  token: string;
  baseUrl?: string;
  sourceId: string;
  contractNumber: string;
  aiworkerUsageId?: string;
  signal?: AbortSignal;
}): Promise<PreviousClaimRow[]> {
  const contractNumber = params.contractNumber.trim();
  if (!contractNumber) return [];

  const { data } = await runClaimIntakeOperation<Record<string, unknown>>({
    session: params.session,
    event: 'previous_claims',
    userInput: {
      source_id: params.sourceId,
      payload: {
        query_parameters: {
          contractNumber,
        },
      },
    },
    token: params.token,
    baseUrl: params.baseUrl,
    aiworkerUsageId: params.aiworkerUsageId,
    signal: params.signal,
  });

  return parsePreviousClaimsResponse(data);
}
